import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AgentActionHistory } from "../utils/models";
import { AgentActionType } from "../utils/models";

interface DemoAgentState {
	data: AgentActionHistory[];
	isPending: boolean;
}

const agentStateCache = new Map<string, { history: AgentActionHistory[]; future: AgentActionHistory[] }>();
// Track which nurse name index to use next for each cache key
const nurseNameIndexCache = new Map<string, number>();
// Track which Call actions are currently "calling" - keyed by cacheKey and action created_at
const callingActions = new Map<string, Set<string>>();
// Map history item created_at to original future item created_at (for preserving nurse names)
const historyToOriginalCreatedAt = new Map<string, string>();
const swappedNurseNames = new Map<string, string>();

const DEFAULT_NURSE_NAMES = ["Harold", "Meagen"];

export const updateHistoryActionsWithNegativeResponse = (cacheKey: string, negativeCount: number) => {
	const cachedState = agentStateCache.get(cacheKey);
	if (cachedState) {
		const updatedHistory = cachedState.history.map((action) => {
			const currentCount = action.negativeResponded ?? 0;
			return {
				...action,
				negativeResponded: Math.max(currentCount, negativeCount), // Use max to ensure we don't decrease
			};
		});
		agentStateCache.set(cacheKey, {
			history: updatedHistory,
			future: cachedState.future,
		});
	}
};

export const swapNurseInHistory = (cacheKey: string, newNurseName: string) => {
	swappedNurseNames.set(cacheKey, newNurseName);
	const cachedState = agentStateCache.get(cacheKey);
	if (cachedState) {
		// Trigger state update by updating the cache
		agentStateCache.set(cacheKey, {
			history: cachedState.history,
			future: cachedState.future,
		});
	}
};

interface UseDemoAgentStateProps {
	cacheKey: string;
	initialFutureData: AgentActionHistory[];
	initialHistoryData?: AgentActionHistory[];
	skipInitialLoading?: boolean;
	skipAnimation?: boolean;
	nurseNames?: string[];
	onEscalate?: (actionType: AgentActionType, count: number, nurseNames?: string[], status?: "loading" | "success" | "error") => void;
}

export const useDemoAgentState = ({
	cacheKey,
	initialFutureData,
	initialHistoryData = [],
	skipInitialLoading = false,
	skipAnimation = false,
	nurseNames = DEFAULT_NURSE_NAMES,
	onEscalate,
}: UseDemoAgentStateProps) => {
	const cachedState = agentStateCache.get(cacheKey);
	const initialHistory = skipAnimation && cachedState?.history && cachedState.history.length > 0 ? cachedState.history : initialHistoryData;
	const initialFuture = cachedState?.future ?? initialFutureData;

	const [agentHistory, setAgentHistory] = useState<DemoAgentState>({ data: initialHistory, isPending: false });
	const [agentFuture, setAgentFuture] = useState<DemoAgentState>({ data: initialFuture, isPending: false });
	const [, setForceUpdate] = useState(0);
	const lastSyncedFutureRef = useRef<string>(JSON.stringify(initialFuture));
	const lastSyncedHistoryRef = useRef<string>(JSON.stringify(initialHistory));
	const callTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
	const lastSwappedNurseRef = useRef<string | undefined>(swappedNurseNames.get(cacheKey));

	useEffect(() => {
		agentStateCache.set(cacheKey, {
			history: agentHistory.data,
			future: agentFuture.data,
		});
		lastSyncedFutureRef.current = JSON.stringify(agentFuture.data);
		lastSyncedHistoryRef.current = JSON.stringify(agentHistory.data);
	}, [cacheKey, agentHistory.data, agentFuture.data]);

	useEffect(() => {
		if (skipInitialLoading) {
			setAgentHistory((prev) => ({ ...prev, isPending: false }));
			setAgentFuture((prev) => ({ ...prev, isPending: false }));
			return;
		}

		const timeout1 = setTimeout(() => {
			setAgentHistory((prev) => ({ ...prev, isPending: false }));
		}, 1000);
		const timeout2 = setTimeout(() => {
			setAgentFuture((prev) => ({ ...prev, isPending: false }));
		}, 2000);

		return () => {
			clearTimeout(timeout1);
			clearTimeout(timeout2);
		};
	}, [skipInitialLoading]);

	useEffect(() => {
		const cachedState = agentStateCache.get(cacheKey);
		if (cachedState) {
			const cachedFutureString = JSON.stringify(cachedState.future);
			if (cachedFutureString !== lastSyncedFutureRef.current) {
				setAgentFuture({ data: cachedState.future, isPending: false });
				lastSyncedFutureRef.current = cachedFutureString;
			}
			const cachedHistoryString = JSON.stringify(cachedState.history);
			if (cachedHistoryString !== lastSyncedHistoryRef.current) {
				setAgentHistory({ data: cachedState.history, isPending: false });
				lastSyncedHistoryRef.current = cachedHistoryString;
			}
		}
		const interval = setInterval(() => {
			const cachedState = agentStateCache.get(cacheKey);
			if (cachedState) {
				const cachedFutureString = JSON.stringify(cachedState.future);
				if (cachedFutureString !== lastSyncedFutureRef.current) {
					setAgentFuture({ data: cachedState.future, isPending: false });
					lastSyncedFutureRef.current = cachedFutureString;
				}
				const cachedHistoryString = JSON.stringify(cachedState.history);
				if (cachedHistoryString !== lastSyncedHistoryRef.current) {
					setAgentHistory({ data: cachedState.history, isPending: false });
					lastSyncedHistoryRef.current = cachedHistoryString;
				}
			}
			const currentSwappedNurse = swappedNurseNames.get(cacheKey);
			if (currentSwappedNurse !== lastSwappedNurseRef.current) {
				lastSwappedNurseRef.current = currentSwappedNurse;
				setForceUpdate((prev) => prev + 1); // Trigger re-render to update nurse names
			}
		}, 100);

		return () => clearInterval(interval);
	}, [cacheKey]);

	const timelineItems = (agentHistory.data ?? []).sort((a, b) => DateTime.fromISO(a.created_at).valueOf() - DateTime.fromISO(b.created_at).valueOf());
	const futureTimelineItems = (agentFuture.data ?? []).sort((a, b) => DateTime.fromISO(a.created_at).valueOf() - DateTime.fromISO(b.created_at).valueOf());

	const getCallNurseName = useCallback(
		(action: AgentActionHistory): string | undefined => {
			if (action.action_type === AgentActionType.Call) {
				const swappedName = swappedNurseNames.get(cacheKey);
				if (swappedName) {
					return swappedName;
				}

				if (nurseNames.length > 0) {
					const originalCreatedAt = historyToOriginalCreatedAt.get(action.created_at);
					const createdAtToUse = originalCreatedAt ?? action.created_at;

					const hash = createdAtToUse.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
					return nurseNames[hash % nurseNames.length];
				}
			}
			return undefined;
		},
		[nurseNames, cacheKey],
	);

	const handleEscalate = useCallback(
		(clickedAction: AgentActionHistory) => {
			if (clickedAction.action_type === AgentActionType.Call) {
				const actionKey = `${clickedAction.created_at}-${clickedAction.action_type}`;

				if (callTimeoutsRef.current.has(actionKey)) {
					return;
				}

				if (!callingActions.has(cacheKey)) {
					callingActions.set(cacheKey, new Set());
				}
				callingActions.get(cacheKey)?.add(clickedAction.created_at);

				const nurseName = getCallNurseName(clickedAction);

				if (onEscalate && nurseName) {
					onEscalate(clickedAction.action_type, clickedAction.target_notification_count ?? 0, [nurseName], "loading");
				}

				setForceUpdate((prev) => prev + 1);

				const timeoutId = setTimeout(() => {
					callTimeoutsRef.current.delete(actionKey);

					setAgentFuture((old) => {
						const clickedIndex = old.data.findIndex(
							(action) => action.created_at === clickedAction.created_at && action.action_type === clickedAction.action_type,
						);

						if (clickedIndex === -1) {
							callingActions.get(cacheKey)?.delete(clickedAction.created_at);
							return old;
						}

						setAgentHistory((prev) => {
							const now = DateTime.now();
							const alreadyExists = prev.data.some((item) => {
								const itemDate = DateTime.fromISO(item.created_at);
								const timeDiff = now.diff(itemDate, "second").seconds;
								return (
									// Within last 15 seconds
									item.action_type === clickedAction.action_type && item.target_notification_count === clickedAction.target_notification_count && timeDiff < 15
								);
							});

							if (alreadyExists) {
								return prev;
							}

							const newHistoryItemCreatedAt = DateTime.now().minus({ seconds: 3 }).toISO();
							const newHistoryItem: AgentActionHistory = {
								...clickedAction,
								created_at: newHistoryItemCreatedAt,
								negativeResponded: 1,
							};

							historyToOriginalCreatedAt.set(newHistoryItemCreatedAt, clickedAction.created_at);

							return { ...prev, data: [...prev.data, newHistoryItem] };
						});

						callingActions.get(cacheKey)?.delete(clickedAction.created_at);

						if (onEscalate && nurseName) {
							onEscalate(clickedAction.action_type, clickedAction.target_notification_count ?? 0, [nurseName], "error");
						}

						return { ...old, data: old.data.filter((_, index) => index !== clickedIndex) };
					});
				}, 10000);

				callTimeoutsRef.current.set(actionKey, timeoutId);
			} else {
				const clickedIndex = futureTimelineItems.findIndex(
					(action) => action.created_at === clickedAction.created_at && action.action_type === clickedAction.action_type,
				);

				const newHistoryItem: AgentActionHistory = {
					...clickedAction,
					created_at: DateTime.now().minus({ seconds: 3 }).toISO(),
				};

				setAgentFuture((old) => ({ ...old, data: old.data.filter((_, index) => index !== clickedIndex) }));

				setAgentHistory((old) => ({ ...old, data: [...old.data, newHistoryItem] }));

				if (onEscalate) {
					onEscalate(newHistoryItem.action_type, newHistoryItem.target_notification_count ?? 0, undefined, "success");
				}
			}
		},
		[futureTimelineItems, onEscalate, cacheKey, getCallNurseName],
	);

	const isCalling = (action: AgentActionHistory): boolean => {
		return action.action_type === AgentActionType.Call && callingActions.get(cacheKey)?.has(action.created_at) === true;
	};

	return {
		agentHistory,
		agentFuture,
		handleEscalate,
		timelineItems,
		futureTimelineItems,
		getCallNurseName,
		isCalling,
	};
};
